import { defineStore } from "pinia";
import { validateTravelDataInput } from "~/utils";

import { formatDate } from "~/utils/date";
import { RATES_INPUT_DATE_FORMAT } from "~/shared/constants";

export const useTravelDataStore = defineStore("travelData", {
  persist: true,
  state: () => ({
    isAppFlow: false,
    searchEngineRate: undefined,
    currentStep: undefined,
    isShowMoreExpanded: false,
    hotelCodes: undefined,
    hotelName: undefined,
    cityId: undefined,
    arrivalDate: new Date(),
    departureDate: new Date(),
    totalGuests: undefined,
    numberOfRooms: 1,
    guestsPerRoom: 1,
    promoCode: undefined,
    selectedRateName: undefined,
    promoCodeWasChanged: false,
    paymentType: undefined,
    bookingCode: undefined,
    selectedPriceType: undefined,
    priceTypeTouched: false,
    propertyType: undefined,
    country: "",
  }),
  getters: {
    getTravelDataObject() {
      return {
        isAppFlow: this.isAppFlow,
        searchEngineRate: this.searchEngineRate,
        currentStep: this.currentStep,
        isShowMoreExpanded: this.isShowMoreExpanded,
        hotelCodes: this.hotelCodes,
        hotelName: this.hotelName,
        arrivalDate: this.arrivalDate,
        departureDate: this.departureDate,
        totalGuests: this.totalGuests,
        numberOfRooms: this.numberOfRooms,
        guestsPerRoom: this.guestsPerRoom,
        promoCode: this.promoCode,
        selectedRateName: this.selectedRateName,
        promoCodeWasChanged: this.promoCodeWasChanged,
        paymentType: this.paymentType,
        bookingCode: this.bookingCode,
        selectedPriceType: this.selectedPriceType,
        priceTypeTouched: this.priceTypeTouched,
        country: this.country,
        propertyType: this.propertyType,
      };
    },
    isInsideABookingStep() {
      const currentStep = this.currentStep;

      const bookingSteps = [
        "rates",
        "addons",
        "details",
        "payment",
        "app/rates",
        "app/addons",
        "app/details",
        "app/payment",
      ];

      if (bookingSteps.includes(currentStep)) {
        return true;
      }

      return false;
    },
    actualBookingStep() {
      if (!this.currentStep && this.isDataValid) {
        return "book/rates";
      }

      if (this.currentStep === "rates") {
        return "book/addons";
      }

      if (this.currentStep === "addons") {
        return "book/details";
      }

      if (this.currentStep === "details") {
        return "book/payment";
      }

      if (this.currentStep === "app") {
        return "app/rates";
      }

      if (this.currentStep === "app/rates") {
        return "app/addons";
      }

      if (this.currentStep === "app/addons") {
        return "app/details";
      }

      if (this.currentStep === "app/details") {
        return "app/payment";
      }

      return "";
    },

    isDataValid(state) {
      try {
        validateTravelDataInput(state);
        return true;
      } catch (e) {
        return false;
      }
    },
    selectedPriceTypeStr(state) {
      switch (state.selectedPriceType) {
        case "pricePerNightInclTax":
          return "website.general.booking.room_from_night_i_tax";

        case "pricePerNightExclTax":
          return "website.general.booking.room_from_night_e_tax";

        case "pricePerStayInclTax":
          return "website.general.booking.room_from_stay_i_tax";

        case "pricePerStayExclTax":
          return "website.general.booking.room_from_stay_e_tax";

        default:
          return "website.general.booking.room_from_night_i_tax";
      }
    },
    getDataForQuery(state) {
      return {
        arrivalDate: formatDate(state.arrivalDate, RATES_INPUT_DATE_FORMAT),
        departureDate: formatDate(state.departureDate, RATES_INPUT_DATE_FORMAT),
        numberOfRooms: state.numberOfRooms,
        totalGuests: state.totalGuests,
      };
    },
    getNightsOfStay(state) {
      const start = new Date(state.arrivalDate);
      const end = new Date(state.departureDate);

      const timeDifference = end - start;

      const nights = Math.ceil(timeDifference / (1000 * 3600 * 24));

      return nights > 0 ? nights : 0;
    },
  },
  actions: {
    async initPriceType(fetchGeoIpLocation) {
      const { countryCode } = await fetchGeoIpLocation();

      if (["US", "CA"].includes(countryCode)) {
        this.selectPriceType("pricePerNightExclTax", false);
      }
    },
    setCurrentStep(step) {
      this.currentStep = step;
    },

    resetTravelData() {
      this.$reset();
    },

    resetTravelDataApp() {
      this.$reset();
      this.currentStep = "app";
      this.userFromApp = true;
    },

    selectPriceType(priceType, isUserAction = true) {
      this.selectedPriceType = priceType;

      if (isUserAction) {
        this.priceTypeTouched = true;
      }
    },
    setBookingCode(bookingCode) {
      this.bookingCode = bookingCode;
    },
    setCartId(cartId) {
      this.cartId = cartId;
    },

    setInitialData() {
      this.currentStep = undefined;
      this.arrivalDate = new Date();
      let tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.departureDate = tomorrow;
    },

    changeToLocationId(locationId) {
      this.hotelCodes = locationId;
    },

    checkTraveldata() {
      const today = new Date();
      let tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (
        Number.isNaN(new Date(this.arrivalDate).valueOf()) ||
        new Date(this.arrivalDate) <= today
      ) {
        this.arrivalDate = today;
      }

      if (
        Number.isNaN(new Date(this.departureDate).valueOf()) ||
        new Date(this.departureDate) <= tomorrow
      ) {
        this.departureDate = tomorrow;
      }

      this.totalGuests = this.totalGuests || 1;
      this.numberOfRooms = this.numberOfRooms || 1;
    },
  },
});
